import { useState, useEffect } from "react";
import { ScryfallCard, ScryfallCardWithCount } from "@/types/scryfall";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { CardSearch } from "@/components/CardSearch";
import { CardList } from "@/components/CardList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useToast } from "@/hooks/useToast";
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { scryfallFormats } from '@/lib/formats';
import { ListControls } from "./ListControls";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Deck, isFormatTag, prepareDeckEvent } from "@/lib/nostr/deck";
import { useAppContext } from "@/hooks/useAppContext";

interface EditListSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deck: Deck | null;
  cards: (ScryfallCard & { count: number; is_foil: string; })[];
  sideboardCards: (ScryfallCard & { count: number; is_foil: string; })[];
}

export const EditListSheet = ({ open, onOpenChange, deck, cards: initialCards, sideboardCards: initialSideboardCards }: EditListSheetProps) => {
  const isMobile = useIsMobile();
  const [cards, setCards] = useState<ScryfallCardWithCount[]>([]);
  const [sideboardCards, setSideboardCards] = useState<ScryfallCardWithCount[]>([]);
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const { toast } = useToast();
  const { config } = useAppContext();

  const [searchQuery, setSearchQuery] = useState("");
  const [title, setTitle] = useState("");
  const [format, setFormat] = useState("");
  const [visibility, setVisibility] = useState<'public' | 'unlisted'>('public');
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);

  const [commanderCardIds, setCommanderCardIds] = useState<string[]>([]);
  const [companionCardId, setCompanionCardId] = useState<string | null>(null);

  const addCard = (card: ScryfallCard) => {
    setCards(prev => {
      const existingCard = prev.find(c => c.id === card.id && c.is_foil === card.is_foil);
      if (existingCard) {
        return prev.map(c => {
          if (c.id === card.id && c.is_foil === card.is_foil) {
            return { ...c, count: c.count + 1 };
          }
          return c;
        });
      }
      return [...prev, { ...card, count: 1 }];
    });
  };

  const removeCard = (cardToRemove: ScryfallCardWithCount) => {
    setCards(prev => {
      const existingCard = prev.find(c => c.id === cardToRemove.id && c.is_foil === cardToRemove.is_foil);
      if (existingCard && existingCard.count > 1) {
        return prev.map(c => {
          if (c.id === cardToRemove.id && c.is_foil === cardToRemove.is_foil) {
            return { ...c, count: c.count - 1 };
          }
          return c;
        });
      }
      return prev.filter(c => c.id !== cardToRemove.id || c.is_foil !== cardToRemove.is_foil);
    });
  };

  const setCardCount = (cardToUpdate: ScryfallCardWithCount, count: number) => {
    setCards(prev => {
      return prev.map(c => {
        if (c.id === cardToUpdate.id && c.is_foil === cardToUpdate.is_foil) {
          return { ...c, count };
        }
        return c;
      });
    });
  };

  const updateCard = (oldCardId: string, newCard: ScryfallCardWithCount) => {
    setCards(prev => prev.map(c => (c.id === oldCardId ? newCard : c)));
  };

  const addSideboardCard = (card: ScryfallCard) => {
    setSideboardCards(prev => {
      const existingCard = prev.find(c => c.id === card.id && c.is_foil === card.is_foil);
      if (existingCard) {
        return prev.map(c => {
          if (c.id === card.id && c.is_foil === card.is_foil) {
            return { ...c, count: c.count + 1 };
          }
          return c;
        });
      }
      return [...prev, { ...card, count: 1 }];
    });
  };

  const removeSideboardCard = (cardToRemove: ScryfallCardWithCount) => {
    setSideboardCards(prev => {
      const existingCard = prev.find(c => c.id === cardToRemove.id && c.is_foil === cardToRemove.is_foil);
      if (existingCard && existingCard.count > 1) {
        return prev.map(c => {
          if (c.id === cardToRemove.id && c.is_foil === cardToRemove.is_foil) {
            return { ...c, count: c.count - 1 };
          }
          return c;
        });
      }
      return prev.filter(c => c.id !== cardToRemove.id || c.is_foil !== cardToRemove.is_foil);
    });
  };

  const setSideboardCardCount = (cardToUpdate: ScryfallCardWithCount, count: number) => {
    setSideboardCards(prev => {
      return prev.map(c => {
        if (c.id === cardToUpdate.id && c.is_foil === cardToUpdate.is_foil) {
          return { ...c, count };
        }
        return c;
      });
    });
  };

  const updateSideboardCard = (oldCardId: string, newCard: ScryfallCardWithCount) => {
    setSideboardCards(prev => prev.map(c => (c.id === oldCardId ? newCard : c)));
  };

  useEffect(() => {
    if (deck && initialCards) {
      setTitle(deck.title);
      setFormat(deck.event.tags.find(isFormatTag)?.[1] || "");
      setVisibility(deck.event.tags.some(t => t[0] === 't' && t[1] === 'unlisted') ? 'unlisted' : 'public');
      setBannerUrl(deck.event.tags.find((t) => t[0] === "banner")?.[1] || null);
      setCards(initialCards);

      if (deck.commanders) {
        const commanderCards = initialCards.filter((c) => deck.commanders.includes(c.name));
        if (commanderCards) setCommanderCardIds(commanderCards.map(c => c.id));
      }

      if (deck.companion) {
        const companionCard = initialCards.find((c) => c.name === deck.companion);
        if (companionCard) setCompanionCardId(companionCard.id);
      }

      if (initialSideboardCards) {
        setSideboardCards(initialSideboardCards);
      }
    }
  }, [deck, initialCards, initialSideboardCards]);

  const handleSave = () => {
    const commanderNames = cards.filter(c => commanderCardIds.includes(c.id)).map(c => c.name);
    const companionName = cards.find(c => c.id === companionCardId)?.name || null;

    const eventToPublish = prepareDeckEvent(
      deck,
      title,
      format,
      cards,
      sideboardCards,
      commanderNames,
      companionName,
      bannerUrl,
      visibility
    );

    publishEvent(eventToPublish, {
      onSuccess: () => {
        toast({ title: "List updated successfully!" });
        onOpenChange(false);
      },
      onError: () => {
        toast({ title: "Failed to update list", variant: "destructive" });
      },
    });
  };

  const handleBuyList = () => {
    const tcgPlayerMassEntryUrl = `${config.tcgPlayerAffiliateUrl}?subId1=deck-list-buy-all&u=https%3A%2F%2Fwww.tcgplayer.com%2Fmassentry%3Fproductline%3DMagic%26c%3D` +
      cards.map(c => {
        const cardName = (c.layout === 'transform' || c.layout === 'modal_dfc' || c.layout === 'flip') ? c.name.split(' // ')[0] : c.name;
        return encodeURIComponent(`1 ${cardName}`);
      }).join('%7C%7C');
    window.open(tcgPlayerMassEntryUrl, '_blank');
  };

  const handleMoveCard = (card: ScryfallCard, from: 'main' | 'sideboard') => {
    if (from === 'main') {
      const cardToMove = cards.find(c => c.id === card.id);
      if (cardToMove) {
        removeCard(cardToMove);
        addSideboardCard(cardToMove);
      }
    } else {
      const cardToMove = sideboardCards.find(c => c.id === card.id);
      if (cardToMove) {
        removeSideboardCard(cardToMove);
        addCard(cardToMove);
      }
    }
  };

  const sideboardCardCount = sideboardCards.reduce((total, card) => total + card.count, 0);
  const mainboardCardCount = cards.reduce((total, card) => total + card.count, 0);

  const renderMobileContent = () => (
    <div className="lg:hidden h-full flex flex-col">
      <Tabs defaultValue="search" className="h-full flex flex-col">
        <TabsContent value="search" forceMount className="flex-grow overflow-y-auto data-[state=inactive]:hidden">
          <div className="flex flex-col gap-4 h-full">
            <CardSearch addCard={addCard} cards={cards} sideboardCards={sideboardCards} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </div>
        </TabsContent>
        <TabsContent value="list" forceMount className="flex-grow overflow-y-auto data-[state=inactive]:hidden">
          <div className="flex flex-col gap-4 h-full">
            <div className="flex gap-2 w-full">
              <div className="flex-grow">
                <Input id="deck-title-mobile" value={title} onChange={e => setTitle(e.target.value)} placeholder="Deck Title" className="h-9" />
              </div>
              <div className="w-[140px] flex-shrink-0">
                <Select onValueChange={setFormat} value={format}>
                  <SelectTrigger id="deck-format-mobile" className="w-full h-9">
                    <SelectValue placeholder="Format" />
                  </SelectTrigger>
                  <SelectContent>
                    {scryfallFormats.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="deck-visibility-mobile" className="block text-sm font-medium text-gray-700">
                Visibility
              </label>
              <Select onValueChange={(value: 'public' | 'unlisted') => setVisibility(value)} value={visibility}>
                <SelectTrigger id="deck-visibility-mobile">
                  <SelectValue placeholder="Visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="unlisted">Unlisted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <CardList
              cards={cards}
              sideboardCards={sideboardCards}
              addCard={addCard}
              removeCard={removeCard}
              setCardCount={setCardCount}
              updateCard={updateCard}
              setBannerUrl={setBannerUrl}
              commanderCardIds={commanderCardIds}
              setCommanderCardIds={setCommanderCardIds}
              companionCardId={companionCardId}
              setCompanionCardId={setCompanionCardId}
              openDeckBuilder={() => {}}
              onMoveCard={(card) => handleMoveCard(card, 'main')}
            />
          </div>
        </TabsContent>
        <TabsList className="grid w-full grid-cols-2 mt-2">
          <TabsTrigger value="search">Search</TabsTrigger>
          <TabsTrigger value="list">List ({mainboardCardCount + sideboardCardCount})</TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );

  const renderDesktopContent = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
      <div className="flex flex-col gap-4 overflow-y-auto pr-2">
        <CardSearch addCard={addCard} cards={cards} sideboardCards={sideboardCards} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      </div>
      <div className="flex flex-col gap-4 overflow-y-auto pr-2">
        <div className="flex gap-2 w-full mb-4">
          <div className="flex-grow">
            <Input id="deck-title-desktop" value={title} onChange={e => setTitle(e.target.value)} placeholder="Deck Title" className="h-9" />
          </div>
          <div className="w-[140px] flex-shrink-0">
            <Select onValueChange={setFormat} value={format}>
              <SelectTrigger id="deck-format-desktop" className="w-full h-9">
                <SelectValue placeholder="Format" />
              </SelectTrigger>
              <SelectContent>
                {scryfallFormats.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mb-4">
          <label htmlFor="deck-visibility" className="block text-sm font-medium text-gray-700">
            Visibility
          </label>
          <Select onValueChange={(value: 'public' | 'unlisted') => setVisibility(value)} value={visibility}>
            <SelectTrigger id="deck-visibility">
              <SelectValue placeholder="Visibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Public</SelectItem>
              <SelectItem value="unlisted">Unlisted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <CardList
          cards={cards}
          addCard={addCard}
          removeCard={removeCard}
          setCardCount={setCardCount}
          updateCard={updateCard}
          setBannerUrl={setBannerUrl}
          commanderCardIds={commanderCardIds}
          setCommanderCardIds={setCommanderCardIds}
          companionCardId={companionCardId}
          setCompanionCardId={setCompanionCardId}
          openDeckBuilder={() => {}}
          onMoveCard={(card) => handleMoveCard(card, 'main')}
        />
        {sideboardCards.length > 0 && (
          <div className="mt-4">
            <h3 className="text-lg font-medium">Sideboard</h3>
            <CardList
              cards={sideboardCards}
              addCard={addSideboardCard}
              removeCard={removeSideboardCard}
              setCardCount={setSideboardCardCount}
              updateCard={updateSideboardCard}
              setBannerUrl={() => {}}
              commanderCardIds={[]}
              setCommanderCardIds={() => {}}
              companionCardId={null}
              setCompanionCardId={() => {}}
              openDeckBuilder={() => {}}
              isSideboard
              onMoveCard={(card) => handleMoveCard(card, 'sideboard')}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-6xl w-full sm:max-w-6xl flex flex-col">
        <SheetHeader>
          <SheetTitle>Edit Deck List</SheetTitle>
          <SheetDescription>
            Add or remove cards, then set your title and format below before saving.
          </SheetDescription>
        </SheetHeader>
        <div className="overflow-hidden flex-grow sm:py-4">
          {isMobile ? renderMobileContent() : renderDesktopContent()}
        </div>
        <SheetFooter className="sm:pt-4 sm:border-t">
          <div className="flex w-full justify-between items-center">
            <div className="w-full sm:w-auto sm:flex-shrink-0">
              <ListControls
                handleSave={handleSave}
                handleShare={() => toast({ title: "Please save your changes before sharing." })}
                handleClearList={() => {
                  setCards([]);
                  setSideboardCards([]);
                  toast({ title: "List cleared for this session." });
                }}
                handleBuyList={handleBuyList}
                isPending={isPending}
              />
            </div>
            <Button
              className="hidden sm:block h-9"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};